import { useMemo } from "react";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { FornecedorDropDown } from "../../../../../../common/domain/models/fornecedor_dropdown";
import { OrdemCompraDropDown } from "../../../../../../common/domain/models/ordem_compra_dropdown";
import { ProdutoServicoDropDown } from "../../../../../../common/domain/models/produto_servico_dropdown";
import { DropDown } from "../../../../../../common/domain/models/dropdown";
import { DefaultMultiAutocomplete } from "../../../../../../common/components/default_multi_autocomplete";
import { DefaultDateRangePickerInput } from "../../../../../../common/components/default_date_range_picker_input";

import CascateList from "../../../../../../common/components/cascate_list";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultInput from "../../../../../../common/components/default_input";

/**
 * Header
 */

type Props = {
  centroCustos: number[] | null;
  titulo: string | null;
  fornecedores: number[] | null;
  dataOrdemCompraDe: Date | null;
  dataOrdemCompraAte: Date | null;
  ordemCompras: number[] | null;
  produtosServicos: number[] | null;
  dataPagoDe: Date | null;
  dataPagoAte: Date | null;
  subcontas: number[] | null;
  statusOC: number[] | null;
  centroCustoDropdown: CentroCustoDropDown[];
  fornecedoresDropdown: FornecedorDropDown[];
  ordemComprasDropdown: OrdemCompraDropDown[];
  produtoServicosDropdown: ProdutoServicoDropDown[];
  subcontasDropdown: DropDown[];
  onCentroCustosChange: (centrosCustos: number[] | null) => void;
  onTituloChange: (titulo: string | null) => void;
  onFornecedoresChange: (fornecedores: number[] | null) => void;
  onDataOrdemCompraChange: (de: Date | null, ate: Date | null) => void;
  onOrdemComprasChange: (ordemCompras: number[] | null) => void;
  onProdutosServicosChange: (produtosServicos: number[] | null) => void;
  onDataPagoChange: (de: Date | null, ate: Date | null) => void;
  onSubcontasChange: (produtosServicos: number[] | null) => void;
  onStatusOCChange: (produtosServicos: number[] | null) => void;
  onClearFilters: () => void;
};

export const Header = ({
  centroCustos,
  titulo,
  fornecedores,
  dataOrdemCompraDe,
  dataOrdemCompraAte,
  ordemCompras,
  produtosServicos,
  dataPagoDe,
  dataPagoAte,
  subcontas,
  statusOC,
  centroCustoDropdown,
  fornecedoresDropdown,
  ordemComprasDropdown,
  produtoServicosDropdown,
  subcontasDropdown,
  onCentroCustosChange,
  onTituloChange,
  onFornecedoresChange,
  onDataOrdemCompraChange,
  onOrdemComprasChange,
  onProdutosServicosChange,
  onDataPagoChange,
  onSubcontasChange,
  onStatusOCChange,
  onClearFilters,
}: Props) => {

  const selectedCentroCustos = useMemo(() => {
    if (centroCustos === null || centroCustos.length === 0) return null;
    if (centroCustoDropdown.length === 0) return null;
    return centroCustoDropdown.filter(x => centroCustos.some(y => x.id === y));
  }, [
    centroCustos,
    centroCustoDropdown,
  ]);

  const selectedFornecedores = useMemo(() => {
    if (fornecedores === null || fornecedores.length === 0) return null;
    if (fornecedoresDropdown.length === 0) return null;
    return fornecedoresDropdown.filter(x => fornecedores.some(y => x.idFornecedor === y));
  }, [
    fornecedores,
    fornecedoresDropdown,
  ]);

  const selectedOrdemCompras = useMemo(() => {
    if (ordemCompras === null || ordemCompras.length === 0) return null;
    if (ordemComprasDropdown.length === 0) return null;
    return ordemComprasDropdown.filter(x => ordemCompras.some(y => x.idOrdemCompra === y));
  }, [
    ordemCompras,
    ordemComprasDropdown,
  ]);

  const selectedProdutosServicos = useMemo(() => {
    if (produtosServicos === null || produtosServicos.length === 0) return null;
    if (produtoServicosDropdown.length === 0) return null;
    return produtoServicosDropdown.filter(x => produtosServicos.some(y => x.idProdutoServico === y));
  }, [
    produtosServicos,
    produtoServicosDropdown,
  ]);

  const selectedSubContas = useMemo(() => {
    if (subcontas === null || subcontas.length === 0) return null;
    if (subcontasDropdown.length === 0) return null;
    return subcontasDropdown.filter(x => subcontas.some(y => x.id === y));
  }, [
    subcontas,
    subcontasDropdown,
  ]);

  const statusOCOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Entregues',
      },
      {
        id: 2,
        label: 'Canceladas',
      },
    ];
  }, [
  ]);

  const selectedStatusOC = useMemo(() => {
    if (statusOC === null || statusOC.length === 0) return null;
    if (statusOCOptions.length === 0) return null;
    return statusOCOptions.filter(x => statusOC.some(y => x.id === y));
  }, [
    statusOC,
    statusOCOptions,
  ]);

  return (
    <CascateList title="Pesquisar" defaultOpen={true}>
      <div className="w-full flex flex-col gap-4">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          <DefaultMultiAutocomplete
            label='Centro de Custo'
            options={centroCustoDropdown}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            value={selectedCentroCustos}
            onChange={e => onCentroCustosChange(e !== null && e.length > 0 ? e.map(x => x.id) : null)}
          />

          <DefaultInput
            name='titulo'
            label='Título'
            value={titulo}
            onChangeValue={e => onTituloChange(e !== null && e.length > 0 ? e : null)}
          />

          <DefaultMultiAutocomplete
            label='Ordem de Compra'
            options={ordemComprasDropdown}
            getOptionKey={x => x.idOrdemCompra}
            getOptionLabel={x => x.descricao}
            value={selectedOrdemCompras}
            onChange={e => onOrdemComprasChange(e !== null && e.length > 0 ? e.map(x => x.idOrdemCompra) : null)}
          />

          <DefaultMultiAutocomplete
            label='Subconta'
            options={subcontasDropdown}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            value={selectedSubContas}
            onChange={e => onSubcontasChange(e !== null && e.length > 0 ? e.map(x => x.id) : null)}
          />

          <DefaultMultiAutocomplete
            label='Fornecedor'
            options={fornecedoresDropdown}
            getOptionKey={x => x.idFornecedor}
            getOptionLabel={x => x.nomeFantasia}
            value={selectedFornecedores}
            onChange={e => onFornecedoresChange(e !== null && e.length > 0 ? e.map(x => x.idFornecedor) : null)}
          />

          <DefaultDateRangePickerInput
            label='Data OC'
            dataDe={dataOrdemCompraDe}
            dataAte={dataOrdemCompraAte}
            onChange={(de, ate) => onDataOrdemCompraChange(de, ate)}
          />

          <DefaultMultiAutocomplete
            label='Produto/Serviço'
            options={produtoServicosDropdown}
            getOptionKey={x => x.idProdutoServico}
            getOptionLabel={x => x.descricao}
            value={selectedProdutosServicos}
            onChange={e => onProdutosServicosChange(e !== null && e.length > 0 ? e.map(x => x.idProdutoServico) : null)}
          />

          <DefaultDateRangePickerInput
            label='Data Pago'
            dataDe={dataPagoDe}
            dataAte={dataPagoAte}
            onChange={(de, ate) => onDataPagoChange(de, ate)}
          />

          <DefaultMultiAutocomplete
            label='Status OC'
            options={statusOCOptions}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.label}
            value={selectedStatusOC}
            onChange={e => onStatusOCChange(e !== null && e.length > 0 ? e.map(x => x.id) : null)}
          />

        </div>

        <div className="w-full flex justify-end">
          <div className="w-[180px]">
            <PrimaryButton
              startIcon={<DynamicIcon nameIcon="ClearRounded" size={25} className="text-black" />}
              buttonColor="yellow"
              onClick={onClearFilters}>
              Limpar filtros
            </PrimaryButton>
          </div>
        </div>
      </div>
    </CascateList>
  );
}